import db from "../db/database";
import {
  CAMPOS_FISCALES_DE_LA_CAJA,
  comportamientoDelPais,
  entidadDelPais,
} from "../config/integracionPorPais";
import {
  fetchFromBusinessCentral,
  parseImportResponse,
  sendToBusinessCentral,
} from "../utils/businessCentralUtils";
import { getCurrentEnvironment } from "../utils/businessCentralUtils";
import { formatAxiosError } from "../utils/common";
import { getPendingSync, isOnline } from "./syncService";
import {
  conAvisoDeBajaRechazada,
  enviarLote,
  motivoDeGuardadoFallido,
  resumenDelLote,
  separarPendientes,
} from "./loteSyncHelpers";
import { anotarError, anotarFila, anotarInicioDeLote, anotarResumenDelLote } from "./syncLog";
import { getTPVById, getTPVByNo } from "./tpvService";
// Nombre con el que esta entidad aparece en las líneas de consola.
const ENTIDAD = "cajas";

// Función helper para buscar un TPV tanto por ID como por No
const findTPV = async (identifier) => {
  try {
    // Primero intentar buscar por ID (número)
    if (typeof identifier === "number" || !isNaN(identifier)) {
      try {
        return await getTPVById(Number(identifier));
      } catch (error) {
        // Si no se encuentra por ID, continuar con la búsqueda por No
      }
    }

    // Buscar por No de Business Central (string)
    return await getTPVByNo(identifier);
  } catch (error) {
    throw new Error(`No se encontró el TPV con identificador ${identifier}`);
  }
};

// Función para transformar una caja al formato esperado por Business Central
// Exportada para poder probarla: las tres grafías de `No` tienen que llevar
// siempre el mismo valor, y nada en el código lo delata si dejan de hacerlo.
export const transformCajaForBC = async (caja) => {
  // Verificar que si hay un TPV, tenga un No de Business Central
  if (caja.TPV) {
    const tpv = await findTPV(caja.TPV);
    if (!tpv) {
      throw new Error(`No se encontró el TPV con identificador ${caja.TPV}`);
    }
    if (!tpv.No) {
      throw new Error(
        `El TPV ${tpv.nombre} no está sincronizado con Business Central. Por favor, sincronícelo primero.`
      );
    }
  }

  // Transformar la caja al formato esperado por BC
  // Una sola expresión para las tres grafías que acepta BC. Antes eran tres
  // independientes: si alguien tocaba una y olvidaba las otras, BC se quedaba
  // con la primera no vacía sin avisar de la divergencia.
  const codigo = caja.No || "TEMP";

  const transformedCaja = {
    No_: codigo,
    "No.": codigo,
    No: codigo,
    Nombre: caja.nombre,
    TPV: caja.TPV ? (await findTPV(caja.TPV)).No : "",
    Deleted: caja.deleted,
  };

  // Crear el formato final esperado por el endpoint
  return {
    data: JSON.stringify({ Cajas: [transformedCaja] }),
  };
};

// Payload con todas las cajas que se manden en la misma llamada.
//
// A diferencia del resto, transformCajaForBC es asíncrona: resuelve la tienda de
// cada caja y falla si esa tienda todavía no tiene No de BC. Por eso el armado
// del lote también lo es.
const transformCajasForBC = async (cajas) => {
  const transformadas = [];

  for (const caja of cajas) {
    const payload = await transformCajaForBC(caja);
    transformadas.push(JSON.parse(payload.data).Cajas[0]);
  }

  return { data: JSON.stringify({ Cajas: transformadas }) };
};

// Nombre con el que se identifica la caja en el cuadro de resultados.
const nombreDeCaja = (caja) => caja.nombre || caja.No || "(sin nombre)";

export const mapCajaFromBC = (apiObj, country) => {
  const base = {
    No: apiObj.No ?? "",
    nombre: apiObj.Nombre ?? "",
    TPV: apiObj.TPV ?? apiObj.Tienda ?? "",
    tpvNombre: apiObj.Nombre ?? "",
  };

  // Sólo la caja de Honduras trae datos fiscales. Los siete campos y el país al
  // que le tocan viven en `config/integracionPorPais.js`.
  if (!comportamientoDelPais(country, "cajaConCamposFiscales")) return base;

  return {
    ...base,
    ...Object.fromEntries(CAMPOS_FISCALES_DE_LA_CAJA.map((c) => [c, apiObj[c] ?? ""])),
  };
};

// BC rechazó la baja: se revierte el borrado local para que la caja vuelva a
// aparecer en el listado y local y BC no queden divergentes. Mismo criterio que
// en el resto de los maestros.
const restoreDeletedCaja = async (caja, reason) => {
  await db.cajas.update(caja.id, {
    deleted: false,
    pendingSync: 0,
    lastModified: new Date(),
    // El motivo que devolvió BC se guarda en el propio registro. Es lo único
    // que sobrevive a la corrida: el registro reaparece en el listado con
    // pendingSync 0, o sea con aspecto de estar perfectamente sincronizado, y
    // sin esto no queda nadie que pueda explicar por qué volvió. Lo lee
    // RecordSyncTag. Ver SANTPV-59.
    syncError: reason || "Business Central no aplicó la baja.",
  });
};

// Sincroniza una sola caja contra BC, para el botón de sincronizar de cada fila
// del listado. Devuelve { ok, message }, igual que los demás maestros.
//
// A diferencia del resto, transformCajaForBC es asíncrona: resuelve la tienda
// de la caja y falla si esa tienda todavía no tiene No de BC. Ese error se
// convierte acá en un mensaje legible en vez de un fallo genérico.
export const syncCajaById = async (cajaId) => {
  if (!isOnline()) {
    return { ok: false, message: "No hay conexión a internet" };
  }

  try {
    const caja = await db.cajas.get(cajaId);
    if (!caja) {
      return { ok: false, message: "Caja no encontrada" };
    }

    if (caja.deleted) {
      // Nunca llegó a BC: sólo existe en local.
      if (!caja.No) {
        await db.cajas.delete(caja.id);
        return { ok: true, message: "Caja eliminada" };
      }

      const response = await sendToBusinessCentral(
        "Importaciones_insertaCajas",
        await transformCajaForBC(caja),
        true
      );
      const result = parseImportResponse(response, caja.No);

      if (!result.ok) {
        await restoreDeletedCaja(caja, result.message);
        return {
          ok: false,
          message:
            result.message || "Business Central no aplicó la baja. La caja sigue en el listado.",
        };
      }

      await db.cajas.delete(caja.id);
      return { ok: true, message: "Caja dada de baja en Business Central" };
    }

    const response = await sendToBusinessCentral(
      "Importaciones_insertaCajas",
      await transformCajaForBC(caja),
      true
    );
    const result = parseImportResponse(response, caja.No || "TEMP");

    if (!result.ok || !result.assignedNo) {
      return {
        ok: false,
        message: result.message || "Respuesta inválida de Business Central",
      };
    }

    // Se lee antes del update, que es justo lo que lo borra.
    const teniaBajaRechazada = Boolean(caja.syncError);

    await db.cajas.update(caja.id, {
      No: result.assignedNo,
      lastModified: new Date(),
      lastSynced: new Date(),
      pendingSync: 0,
      syncError: null,
    });

    return {
      ok: true,
      message: conAvisoDeBajaRechazada("Caja sincronizada correctamente.", teniaBajaRechazada),
    };
  } catch (error) {
    anotarError(ENTIDAD, `falló el envío del registro ${cajaId}`, error);
    return {
      ok: false,
      // transformCajaForBC lanza Error con un mensaje propio (ej. la tienda sin
      // sincronizar); formatAxiosError lo respeta y sólo desarma los de axios.
      message: formatAxiosError(error, "No se pudo sincronizar la caja"),
    };
  }
};

export const syncCajas = async () => {
  if (!isOnline()) {
    return false;
  }

  // Cajas cuyo push a BC falló (o fue ignorado por BC) en esta corrida.
  // Cada entrada es { caja, action: "delete" | "save", reason }. Antes los
  // errores sólo se escribían en consola -- y se mostraban con messageApi desde
  // el propio servicio -- y la función devolvía true igual.
  const failedCajas = [];

  // Una fila por registro enviado, para el cuadro que se le muestra a quien
  // apretó Sincronizar. Incluye los correctos, no sólo los fallos.
  const detalle = [];

  try {
    const env = await getCurrentEnvironment();
    const pendingCajas = await getPendingSync("cajas");

    if (pendingCajas.length > 0) {
      const { soloLocales, aEnviar } = separarPendientes(pendingCajas);

      for (const caja of soloLocales) {
        await db.cajas.delete(caja.id);
        detalle.push(
          anotarFila(
            ENTIDAD,
            {
              registro: nombreDeCaja(caja),
              no: null,
              accion: "deleted",
              ok: true,
              mensaje: "Eliminado sólo en local: nunca llegó a Business Central",
            },
            caja.id,
            "baja-local"
          )
        );
      }

      if (aEnviar.length > 0) {
        anotarInicioDeLote(ENTIDAD, aEnviar.length);

        // UNA llamada para todos los pendientes, en vez de una por registro.
        const lote = await enviarLote(
          "Importaciones_insertaCajas",
          await transformCajasForBC(aEnviar),
          aEnviar.length,
          "cajas"
        );

        // El cruce es por posición: BC devuelve los resultados en el mismo orden
        // en que recibió los registros. Por No_ no se podría, porque todas las
        // altas se mandan como "TEMP" y volverían indistinguibles entre sí.
        for (let indice = 0; indice < aEnviar.length; indice++) {
          const caja = aEnviar[indice];
          const resultado = lote.results[indice];
          let traiaBajaRechazada = false;

          if (caja.deleted) {
            if (resultado.ok) {
              await db.cajas.delete(caja.id);
            } else {
              // BC no aplicó la baja: el registro NO se borra en local, se
              // revierte el borrado y el motivo queda guardado en él.
              await restoreDeletedCaja(caja, resultado.message);
              failedCajas.push({ caja, action: "delete", reason: resultado.message });
            }
          } else if (resultado.ok && resultado.assignedNo) {
            // Se anota antes del update, que es lo que borra el motivo anterior.
            traiaBajaRechazada = Boolean(caja.syncError);

            await db.cajas.update(caja.id, {
              No: resultado.assignedNo,
              lastModified: new Date(),
              lastSynced: new Date(),
              pendingSync: 0,
              syncError: null,
            });
          } else {
            const motivo = motivoDeGuardadoFallido(resultado);
            failedCajas.push({ caja, action: "save", reason: motivo });
          }

          detalle.push(
            anotarFila(
              ENTIDAD,
              {
                registro: nombreDeCaja(caja),
                no: resultado.assignedNo || caja.No || null,
                accion: resultado.action || (caja.deleted ? "deleted" : "saved"),
                ok: caja.deleted ? resultado.ok : resultado.ok && Boolean(resultado.assignedNo),
                mensaje: conAvisoDeBajaRechazada(resultado.message || "", traiaBajaRechazada),
              },
              caja.id,
              caja.deleted ? "baja" : undefined
            )
          );
        }
      }
    }

    // Obtener cajas del servidor después de sincronizar las pendientes. El
    // nombre de la entidad es del país --Honduras publica `CajaTPV`-- y vive en
    // `config/integracionPorPais.js` con los nueve declarados, para que un país
    // sin decidir no caiga en silencio al nombre de los demás.
    const { endpoint } = entidadDelPais(env.country, "cajas");

    const serverCajas = await fetchFromBusinessCentral(endpoint);

    const localCajas = await db.cajas.toArray();

    // Mapa para buscar cajas locales por No rápidamente
    const localCajasByNo = {};
    localCajas.forEach((caja) => {
      if (caja.No) {
        localCajasByNo[caja.No] = caja;
      }
    });

    // Actualizar cajas existentes y añadir nuevas desde el servidor
    for (const serverCajaRaw of serverCajas) {
      const serverCaja = mapCajaFromBC(serverCajaRaw, env.country);
      const localCaja = localCajasByNo[serverCaja.No];

      if (localCaja) {
        if (localCaja.pendingSync === 0) {
          await db.cajas.update(localCaja.id, {
            ...serverCaja,
            lastModified: new Date(),
            lastSynced: new Date(),
          });
        }
      } else {
        await db.cajas.add({
          ...serverCaja,
          lastModified: new Date(),
          lastSynced: new Date(),
          pendingSync: 0,
          syncError: null,
          deleted: false,
        });
      }
    }

    // Cierra el lote en consola con el mismo recuento que ve quien apretó

    // Sincronizar. Antes había que sumar las líneas a ojo.

    const resumen = resumenDelLote(detalle);

    anotarResumenDelLote(ENTIDAD, resumen);

    if (failedCajas.length > 0) {
      const deletes = failedCajas.filter((f) => f.action === "delete");
      const saves = failedCajas.filter((f) => f.action === "save");

      const parts = [];
      if (deletes.length > 0) {
        parts.push(
          `${deletes.length} caja(s) no se pudieron eliminar en Business Central y siguen en el listado`
        );
      }
      if (saves.length > 0) {
        parts.push(`${saves.length} caja(s) no se pudieron guardar en Business Central`);
      }

      // Motivo concreto que devolvió BC, si lo hubo.
      const reason = failedCajas.find((f) => f.reason)?.reason;

      return {
        success: false,
        failedCount: failedCajas.length,
        message: reason ? `${parts.join(". ")}: ${reason}` : parts.join(". "),
        lote: resumen,
      };
    }

    // Se devuelve el detalle también cuando todo salió bien: la pantalla muestra
    // el mismo cuadro en los dos casos, y `executeWithRetry` sólo mira
    // `success === false`, así que un objeto acá no cambia nada para syncAll.
    return { success: true, lote: resumen };
  } catch (error) {
    anotarError(ENTIDAD, "falló la sincronización", error);
    return false;
  }
};
